import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = new Map();
  /**
   * Bumped on every click so a slow response from a previous open can never morph itself into the
   * dialog after the shopper has moved on to another product.
   */
  #requestId = 0;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    const baseHref = productLink?.href || this.dataset.productUrl;
    if (!baseHref) return '';

    const url = new URL(baseHref, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    // Start fetching as soon as the finger lands on the button: pointerdown fires well before the
    // click does, so the product page is already in flight by the time the dialog opens.
    this.addEventListener('pointerdown', this.#prefetchProductPage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerdown', this.#prefetchProductPage);
    this.#abortController?.abort();
  }

  #prefetchProductPage = () => {
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.has(url)) return;

    // Nothing awaits a prefetch, so swallow its failures; the click path reports them.
    this.#loadProductGrid(url).catch(() => {});
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    const requestId = ++this.#requestId;
    const cached = this.#cachedContent.get(currentUrl);

    // Open first, fill in after. A product page is around a megabyte of HTML, so awaiting the fetch
    // before calling showDialog() left the tap with no feedback at all for a second or more on the
    // first open of a session.
    if (!cached) this.#setLoading(true);
    this.#openQuickAddModal();

    try {
      const productGrid = cached ?? (await this.#loadProductGrid(currentUrl));

      // Another product was opened while this one was in flight.
      if (requestId !== this.#requestId) return;

      if (!productGrid) {
        this.#closeQuickAddModal();
        return;
      }

      // Use a fresh clone from the cache
      await this.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
    } catch (error) {
      if (requestId === this.#requestId) this.#closeQuickAddModal();
      throw error;
    } finally {
      if (requestId === this.#requestId) this.#setLoading(false);
    }
  };

  /**
   * Returns the product grid for a URL, fetching it once and caching it. Concurrent callers (a
   * prefetch and the click it precedes) share the same in-flight request instead of racing.
   * @param {string} productPageUrl - The URL of the product page to load
   * @returns {Promise<Element | null>}
   */
  #loadProductGrid(productPageUrl) {
    const cached = this.#cachedContent.get(productPageUrl);
    if (cached) return Promise.resolve(cached);

    const pending = this.#pendingContent.get(productPageUrl);
    if (pending) return pending;

    const request = this.fetchProductPage(productPageUrl)
      .then((html) => {
        const gridElement = html?.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;

        // Cache the cloned element to avoid modifying the original
        const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(productPageUrl, productGrid);
        return productGrid;
      })
      .finally(() => this.#pendingContent.delete(productPageUrl));

    this.#pendingContent.set(productPageUrl, request);

    return request;
  }

  /**
   * Toggles the dialog's skeleton placeholder.
   * @param {boolean} loading - Whether the dialog is waiting on content
   */
  #setLoading(loading) {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent) return;

    dialogComponent.toggleAttribute('loading', loading);
    dialogComponent.refs?.dialog?.setAttribute('aria-busy', String(loading));
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      // Nova PDP uses <nova-price-pdp>; Dawn-style templates use <product-price>
      const productPrice =
        productGrid.querySelector('product-price') || productGrid.querySelector('nova-price-pdp');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }

      // Hoist the whole buy-buttons block, not just <product-form-component>: the block's stylesheet is
      // scoped to `.buy-buttons-block`, so detaching the form from that wrapper drops the single-row
      // layout (quantity + add to cart + wishlist) and the square sizing of the wishlist app block.
      const buyButtons = productFormComponent.closest('.buy-buttons-block') || productFormComponent;

      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(buyButtons);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  showDialog() {
    const { dialog } = this.refs;
    const wasOpen = dialog.open;

    super.showDialog();

    if (wasOpen || !dialog.open) return;

    /**
     * showModal() lands focus on the first focusable child, which here is the close button. Safari
     * counts that programmatic focus as :focus-visible where Chrome does not, so the theme's
     * `*:focus-visible` rule paints a black ring around the X on iOS (and, below iOS 16.4, the
     * ::after border workaround in base.css). Parking focus on the dialog instead keeps the modal
     * announced and still puts the close button first in the tab order -- with its ring intact for
     * keyboard users -- without the stray outline on open.
     */
    dialog.focus({ preventScroll: true });
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
